import { useRef, useState } from 'react';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import IosShareRoundedIcon from '@mui/icons-material/IosShareRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import type { EmbedShareInfo } from '@/types/embed.types';

interface EmbedFooterProps {
  /**
   * Returns the public share URL and paste-ready iframe snippet. Omitted in
   * public viewer flow because the recipient is already inside the shared view.
   */
  getEmbedInfo?: () => Promise<EmbedShareInfo>;
}

export function EmbedFooter({ getEmbedInfo }: EmbedFooterProps) {
  const [info, setInfo] = useState<EmbedShareInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedField, setCopiedField] = useState<'url' | 'snippet' | null>(null);
  const getEmbedInfoRef = useRef(getEmbedInfo);

  getEmbedInfoRef.current = getEmbedInfo;

  const handleLoadInfo = async () => {
    setBusy(true);
    setError(null);
    try {
      const next = await getEmbedInfoRef.current?.();
      if (!next) return;
      setInfo(next);
    } catch (err) {
      setError((err as Error)?.message || 'Failed to prepare share link');
    } finally {
      setBusy(false);
    }
  };

  if (!getEmbedInfo) return null;

  const handleCopy = async (text: string, field: 'url' | 'snippet') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField((curr) => (curr === field ? null : curr)), 1500);
    } catch {
      setError("Couldn't copy. Press Cmd/Ctrl+C with the field selected.");
    }
  };

  return (
    <Box
      component="footer"
      sx={{
        flexShrink: 0,
        px: 3,
        py: 1.5,
        borderTop: '1px solid rgba(255,255,255,0.08)',
        backgroundColor: 'rgba(255,255,255,0.02)',
      }}
    >
      {!info ? (
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'flex-start', md: 'center' }}
          justifyContent="space-between"
          gap={1.5}
        >
          <Box>
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Share this view
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.45)', fontSize: 12, mt: 0.25 }}>
              Create or load the public link only when you request it.
            </Typography>
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<IosShareRoundedIcon />}
            onClick={() => { void handleLoadInfo(); }}
            disabled={busy}
            sx={{
              bgcolor: '#8ff5ff',
              color: '#003f43',
              fontWeight: 600,
              '&:hover': { bgcolor: '#69edf9' },
            }}
          >
            {busy ? 'Preparing...' : 'Show Share Link'}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.25}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Share link
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                variant="text"
                startIcon={<OpenInNewRoundedIcon />}
                href={info.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: '#8ff5ff' }}
              >
                Open
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<ContentCopyRoundedIcon />}
                onClick={() => { void handleCopy(info.url, 'url'); }}
              >
                {copiedField === 'url' ? 'Copied' : 'Copy Link'}
              </Button>
            </Stack>
          </Stack>
          <TextField
            value={info.url}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& input': {
                fontFamily: 'monospace',
                fontSize: 11,
                color: 'rgba(143,245,255,0.85)',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0,0,0,0.32)',
              },
            }}
          />
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1} flexWrap="wrap">
            <Typography
              sx={{
                color: 'rgba(255,255,255,0.55)',
                fontSize: 11,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Embed code
            </Typography>
            <Button
              size="small"
              variant="text"
              startIcon={<ContentCopyRoundedIcon />}
              onClick={() => { void handleCopy(info.snippet, 'snippet'); }}
              sx={{ color: '#8ff5ff' }}
            >
              {copiedField === 'snippet' ? 'Copied' : 'Copy Code'}
            </Button>
          </Stack>
          <TextField
            value={info.snippet}
            multiline
            minRows={2}
            maxRows={4}
            fullWidth
            size="small"
            InputProps={{ readOnly: true }}
            sx={{
              '& textarea': {
                fontFamily: 'monospace',
                fontSize: 11,
                color: 'rgba(143,245,255,0.85)',
              },
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'rgba(0,0,0,0.32)',
              },
            }}
          />
        </Stack>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1, borderRadius: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}

export default EmbedFooter;
